import { Injectable, Type } from '@angular/core';
import { environment } from '../../../../../environments/environment';

import { ExternalPrefillProvider } from './external-prefill-provider.model';
import { ProjectCreationWorkflow } from './project-creation-workflow.model';
import { TEST_WORKFLOW } from './test-workflow.constant';
import { VidaApplicationCardComponent } from './vida/vida-application-card.component';
import { VidaPrefillProvider } from './vida/vida-prefill-provider';
import { VIDA_WORKFLOW } from './vida/vida-workflow.constant';

/**
 * A workflow plus its Angular-side bindings.
 *
 * The rule half lives in the pure `ProjectCreationWorkflow`; the injectable bits are added here so
 * `resolve-rule.ts` stays free of `@angular/core` and remains runnable under `npm run test:pure`.
 *
 * `prefillProvider` is a `Type<>`, not an instance: `confirm()` calls our API and therefore needs
 * `HttpClient`, which a module-level constant cannot inject.
 */
export interface RegisteredWorkflow extends ProjectCreationWorkflow {
  prefillProvider?: Type<ExternalPrefillProvider>;
  /** Renders the provider's read-only card. Hosted via `ngComponentOutlet` in FM11. */
  blockComponent?: Type<unknown>;
}

/** VIDA with its Angular bindings (DT-6472 FM1) — the first REAL production workflow. */
const VIDA_REGISTERED: RegisteredWorkflow = {
  ...VIDA_WORKFLOW,
  prefillProvider: VidaPrefillProvider,
  blockComponent: VidaApplicationCardComponent
};

/**
 * The project-creation workflow registry.
 *
 * Production registers the VIDA workflow (DT-6472) — since that landed, "existing programs
 * behaviorally unchanged" (design FR-16: programs without the key are unaffected) is held by
 * KEY MATCHING plus the base-rule regression spec in `resolve-rule.pure-spec.ts`, no longer by
 * an empty registry. Only a program whose `projectCreationWorkflowKey` names a registered key
 * gets any workflow behavior.
 *
 * The synthetic TEST_WORKFLOW stays NON-PRODUCTION-ONLY, for QA of the overlay machinery — see
 * `test-workflow.constant.ts`.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowRegistryService {

  private readonly workflows: RegisteredWorkflow[] =
    environment.production ? [VIDA_REGISTERED] : [VIDA_REGISTERED, TEST_WORKFLOW];

  /** Every registered workflow. Passed to `resolveRule`, which takes the registry as a parameter. */
  all(): RegisteredWorkflow[] {
    return this.workflows;
  }

  /** Looks a workflow up by key. Undefined for an unknown key — callers fall back to the base rule. */
  byKey(key: string | null | undefined): RegisteredWorkflow | undefined {
    return key ? this.workflows.find(workflow => workflow.key === key) : undefined;
  }

  /**
   * Options for the SYSTEM_ADMIN config dropdown (FM8). `null` is "Default" — no workflow — and is
   * always offered, so a configured program can always be returned to default behavior.
   */
  options(): { value: string | null; label: string }[] {
    return [{ value: null, label: 'Default' }]
      .concat(this.workflows.map(workflow => ({ value: workflow.key, label: workflow.key })));
  }
}
